import { useState } from "react";
import { ReviewTileItem } from "@/types/tile.types";
import { useClipboard } from "./useClipboard";

export interface UseTileCopyOptions {
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

const formatTileContentForCopy = (tile: ReviewTileItem): string => {
  let html = "";
  const originalData = tile.originalData || {};

  if (tile.clause) {
    html += `<p><strong>Clause:</strong> ${tile.clause}</p>`;
  }

  if (originalData.ambiguousText) {
    html += `<p><strong>Ambiguous Text:</strong> ${originalData.ambiguousText}</p>`;
  }
  if (originalData.complexText) {
    html += `<p><strong>Complex Text:</strong> ${originalData.complexText}</p>`;
  }
  if (originalData.riskText) {
    html += `<p><strong>Risk Text:</strong> ${originalData.riskText}</p>`;
  }
  if (originalData.nonStandardText) {
    html += `<p><strong>Non-Standard Text:</strong> ${originalData.nonStandardText}</p>`;
  }
  if (originalData.text1) {
    html += `<p><strong>Text 1:</strong> ${originalData.text1}</p>`;
  }
  if (originalData.text2) {
    html += `<p><strong>Text 2:</strong> ${originalData.text2}</p>`;
  }

  if (tile.reason) {
    html += `<p><strong>Reason:</strong> ${tile.reason}</p>`;
  }

  if (tile.analysis) {
    html += `<p><strong>Analysis:</strong> ${tile.analysis}</p>`;
  }

  if (tile.explanation) {
    html += `<p><strong>Explanation:</strong> ${tile.explanation}</p>`;
  }

  if (originalData.riskExplanation) {
    html += `<p><strong>Risk Explanation:</strong> ${originalData.riskExplanation}</p>`;
  }

  const suggestedClause =
    originalData.suggestedclause ||
    originalData.suggestion ||
    originalData.suggestions ||
    originalData.suggestedNumber ||
    originalData.additionalClause ||
    originalData.missingClause ||
    null;

  if (suggestedClause) {
    html += `<p><strong>Suggestion:</strong> ${suggestedClause}</p>`;
  }

  return html;
};

const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

export const useTileCopy = (options?: UseTileCopyOptions) => {
  const [isCopying, setIsCopying] = useState(false);
  const [copyingTileId, setCopyingTileId] = useState<string | null>(null);
  const { copyToClipboard, copyHtmlToClipboard } = useClipboard();

  const copySingleTile = async (tile: ReviewTileItem): Promise<boolean> => {
    setCopyingTileId(tile.id);
    setIsCopying(true);
    try {
      const htmlContent = formatTileContentForCopy(tile);
      const plainText = stripHtml(htmlContent);
      const success = await copyHtmlToClipboard(
        htmlContent,
        plainText,
        `Tile "${tile.clause || tile.id}" copied to clipboard`
      );
      if (success) {
        options?.onSuccess?.(`Tile "${tile.clause || tile.id}" copied to clipboard`);
      } else {
        options?.onError?.("Failed to copy tile to clipboard");
      }
      return success;
    } catch (error: any) {
      console.error("Error copying tile:", error);
      options?.onError?.(`Failed to copy tile: ${error.message}`);
      return false;
    } finally {
      setCopyingTileId(null);
      setIsCopying(false);
    }
  };

  const copyMultipleTiles = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (tiles.length === 0) {
      options?.onError?.("No tiles to copy");
      return false;
    }

    setIsCopying(true);

    try {
      const allTilesHtml = tiles
        .map((tile) => formatTileContentForCopy(tile))
        .join("<hr>");
      const plainText = stripHtml(allTilesHtml);
      const success = await copyHtmlToClipboard(
        allTilesHtml,
        plainText,
        `${tiles.length} tiles copied to clipboard`
      );

      if (success) {
        options?.onSuccess?.(`${tiles.length} tiles copied to clipboard`);
      } else {
        options?.onError?.("Failed to copy tiles to clipboard");
      }
      return success;
    } catch (error: any) {
      console.error("Error copying tiles:", error);
      options?.onError?.(`Failed to copy tiles: ${error.message}`);
      return false;
    } finally {
      setIsCopying(false);
    }
  };

  const copyTilesAsTable = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (tiles.length === 0) {
      options?.onError?.("No tiles to copy");
      return false;
    }

    setIsCopying(true);

    try {
      let tableText = "Clause/Section\tImpact\tReason/Analysis\n";
      tableText += "---\t---\t---\n";

      tiles.forEach((tile) => {
        const clause = tile.clause || "N/A";
        const impact = tile.impact && tile.impact !== "ALL" ? tile.impact : "N/A";
        const reason = tile.reason || tile.analysis || tile.explanation || "N/A";

        tableText += `${clause}\t${impact}\t${reason}\n`;
      });

      const success = await copyToClipboard(tableText, `${tiles.length} tiles copied as table format`);

      if (success) {
        options?.onSuccess?.(`${tiles.length} tiles copied as table format`);
      } else {
        options?.onError?.("Failed to copy tiles as table");
      }
      return success;
    } catch (error: any) {
      console.error("Error copying tiles as table:", error);
      options?.onError?.(`Failed to copy tiles as table: ${error.message}`);
      return false;
    } finally {
      setIsCopying(false);
    }
  };

  const copyTilesAsJSON = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (tiles.length === 0) {
      options?.onError?.("No tiles to copy");
      return false;
    }

    setIsCopying(true);

    try {
      const jsonText = JSON.stringify(
        tiles.map((t) => t.originalData),
        null,
        2
      );
      const success = await copyToClipboard(jsonText, `${tiles.length} tiles copied as JSON`);

      if (success) {
        options?.onSuccess?.(`${tiles.length} tiles copied as JSON`);
      } else {
        options?.onError?.("Failed to copy tiles as JSON");
      }
      return success;
    } catch (error: any) {
      console.error("Error copying tiles as JSON:", error);
      options?.onError?.(`Failed to copy tiles as JSON: ${error.message}`);
      return false;
    } finally {
      setIsCopying(false);
    }
  };

  const copyTilesSummary = async (tiles: ReviewTileItem[]): Promise<boolean> => {
    if (tiles.length === 0) {
      options?.onError?.("No tiles to copy");
      return false;
    }

    setIsCopying(true);

    try {
      const impactCounts = tiles.reduce(
        (acc, tile) => {
          const impact = tile.impact || "UNKNOWN";
          acc[impact] = (acc[impact] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>
      );

      let summaryText = `Review Summary (${tiles.length} items)\n`;
      summaryText += "=".repeat(40) + "\n\n";

      summaryText += "Impact Breakdown:\n";
      Object.entries(impactCounts).forEach(([impact, count]) => {
        if (impact !== "ALL") {
          summaryText += `- ${impact}: ${count} items\n`;
        }
      });
      summaryText += "\n";

      summaryText += "Items:\n";
      tiles.forEach((tile, index) => {
        const clause = tile.clause || `Item ${index + 1}`;
        const impact = tile.impact && tile.impact !== "ALL" ? ` [${tile.impact}]` : "";
        summaryText += `${index + 1}. ${clause}${impact}\n`;
      });

      const success = await copyToClipboard(
        summaryText,
        `Summary of ${tiles.length} tiles copied to clipboard`
      );

      if (success) {
        options?.onSuccess?.(`Summary of ${tiles.length} tiles copied to clipboard`);
      } else {
        options?.onError?.("Failed to copy tiles summary");
      }
      return success;
    } catch (error: any) {
      console.error("Error copying tiles summary:", error);
      options?.onError?.(`Failed to copy tiles summary: ${error.message}`);
      return false;
    } finally {
      setIsCopying(false);
    }
  };

  return {
    isCopying,
    copyingTileId,
    copySingleTile,
    copyMultipleTiles,
    copyTilesAsTable,
    copyTilesAsJSON,
    copyTilesSummary,
  };
};
